/**
 * The care-plan board — a stay's week of interprofessional care, drawn from the
 * platform's fold and changed one command at a time.
 *
 * WHAT IT DRAWS is `CarePlan.items`, exactly. There is no client-side model of
 * the plan, no local copy the board keeps between edits, and no optimistic row:
 * every edit states one intent, the hook applies it to the item list and writes
 * that list against the version it was read at, the server answers with the new
 * plan, and the board redraws from that. The whole-state
 * `onStateUpdate(newState)` this replaces is why two nurses editing one plan
 * used to overwrite each other in silence.
 *
 * ROWS ARE DISCIPLINES, and a discipline is a ROLE in the org
 * (`GET /roles` — config/app.yaml `care_roles.lead` says so to the model in the
 * same words: "Set each intervention's discipline_id to one of the role IDs
 * below"). adr/0021 places it: a vocabulary that is true whoever the patient is
 * belongs to the knowledge plane, not inside one patient's plan. So the board
 * has no "add discipline": creating a discipline is creating a role for the
 * whole organisation, which is the org surface's job and not something to do by
 * accident while planning one patient's Tuesday.
 *
 * A DISCIPLINE THE ROLE LIST DOES NOT NAME still gets a row. An intervention
 * whose `discipline_id` matches no role would otherwise be planned care that no
 * screen renders, which is worse than an unfamiliar row label: the row appears,
 * labelled with the id, and the nurse can see and remove it.
 *
 * COLUMNS ARE ALL SEVEN DAYS. See CARE_PLAN_DAYS in api/endpoints/carePlan.ts:
 * the server stores a plan written on a Saturday, so a Monday-to-Friday board
 * would hide it.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Link, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { randomUUID } from '@/lib/uuid';
import {
  CARE_PLAN_DAYS,
  type CarePlan,
  type CarePlanDay,
  type CarePlanItem,
} from '@/api/endpoints/carePlan';
import type { OccurrenceStatus } from '@/api/endpoints/plans';
import { CarePlanInterventionGoneError, type CarePlanIntent } from '@/hooks/useCarePlan';

/** One row of the board: a role of the organisation, as the plan names it. */
export interface CarePlanDiscipline {
  id: string;
  name: string;
}

/** One person the board may put in a cell. */
export interface CarePlanPerformer {
  id: string;
  name: string;
}

export interface CarePlanBoardProps {
  /**
   * The stay's plan, or null when nobody has planned this stay yet. Null draws
   * an empty week, and the first edit creates the plan.
   */
  plan: CarePlan | null;
  /** The org's clinical roles. Empty is a real state — see the empty row below. */
  disciplines: CarePlanDiscipline[];
  /** Who staffs this patient's unit, and therefore who an act can be given to. */
  performers: CarePlanPerformer[];
  /** The config-driven label of the real patient. Null before it resolves. */
  patientLabel: string | null;
  /** False for a reader without editor rights on the patient. */
  isEditable: boolean;
  /**
   * What was actually done this week, keyed by item id. Empty is a real state:
   * a plan not yet in force has no occurrences, and neither has one whose
   * activities are all still ahead.
   */
  execution: ReadonlyMap<string, OccurrenceStatus>;
  /**
   * Send one command. Rejects with CarePlanInterventionGoneError when what the
   * nurse pointed at has been removed by somebody else, and with the API's own
   * error otherwise. It never resolves without the change having landed.
   */
  onApply: (intent: CarePlanIntent) => Promise<CarePlan>;
}

/**
 * Weekday labels in the reader's own locale, from Intl rather than from a
 * translated list: seven day names per locale in three locale files is seven
 * chances for one of them to be wrong, and the browser already has them.
 *
 * 2024-01-01 was a Monday, so adding the day's index to it lands on that day.
 */
function weekdayLabels(locale: string): Record<CarePlanDay, string> {
  const format = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  return Object.fromEntries(
    CARE_PLAN_DAYS.map((day, index) => [
      day,
      format.format(new Date(Date.UTC(2024, 0, 1 + index))),
    ]),
  ) as Record<CarePlanDay, string>;
}

/** HH:MM sorts lexicographically, so the cell reads down the day in order. */
function byTime(a: CarePlanItem, b: CarePlanItem): number {
  return a.time.localeCompare(b.time);
}

/**
 * The rows to draw: every role, plus a row for any `discipline_id` the plan
 * uses that the role list does not name.
 */
function boardRows(
  items: readonly CarePlanItem[],
  disciplines: CarePlanDiscipline[],
): CarePlanDiscipline[] {
  const known = new Set(disciplines.map((d) => d.id));
  const unknown = new Map<string, CarePlanDiscipline>();
  for (const iv of items) {
    if (known.has(iv.discipline_id) || unknown.has(iv.discipline_id)) continue;
    unknown.set(iv.discipline_id, { id: iv.discipline_id, name: iv.discipline_id });
  }
  return [...disciplines, ...unknown.values()];
}

/** The fields an add or a retime fills in. */
interface ScheduleDraft {
  day: CarePlanDay;
  time: string;
  duration_minutes: string;
}

const EMPTY_DRAFT: ScheduleDraft = { day: 'monday', time: '', duration_minutes: '' };

export function CarePlanBoard({
  plan,
  disciplines,
  performers,
  patientLabel,
  isEditable,
  execution,
  onApply,
}: CarePlanBoardProps) {
  const { t, i18n } = useTranslation();
  const dayLabels = useMemo(() => weekdayLabels(i18n.language), [i18n.language]);
  const items = useMemo(() => plan?.items ?? [], [plan]);
  const rows = useMemo(() => boardRows(items, disciplines), [items, disciplines]);

  const [activeDay, setActiveDay] = useState<CarePlanDay>(CARE_PLAN_DAYS[0]);
  const [linkCopied, setLinkCopied] = useState(false);

  // The cell an add was started from. Null when the add dialog is closed —
  // the dialog's open state IS this, so the two can never disagree about which
  // row and day a new intervention lands in.
  const [addAt, setAddAt] = useState<{ disciplineId: string; day: CarePlanDay } | null>(null);
  const [addTitle, setAddTitle] = useState('');
  const [addSchedule, setAddSchedule] = useState<ScheduleDraft>(EMPTY_DRAFT);
  const [addAssignee, setAddAssignee] = useState('');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editSchedule, setEditSchedule] = useState<ScheduleDraft>(EMPTY_DRAFT);

  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // The selected intervention is READ OUT OF THE PLAN each render, never copied
  // into state. A copy is what let a detail dialog keep showing an act after a
  // command had removed it: the plan is the one source, so when it stops holding
  // this id the dialog closes on the next render instead of editing a ghost.
  const selected = selectedId
    ? (items.find((iv) => iv.id === selectedId) ?? null)
    : null;

  /**
   * Run one command, and say what happened when it does not land.
   *
   * Every failure keeps the editor open. The only two outcomes a nurse can act
   * on are "it saved" and "it did not, and here is why"; a dialog that closes
   * on a rejection tells her it saved.
   */
  const runIntent = async (intent: CarePlanIntent, onLanded: () => void) => {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      await onApply(intent);
      onLanded();
    } catch (error) {
      if (error instanceof CarePlanInterventionGoneError) {
        // Somebody removed it while she was working on it. The board has already
        // re-read, so it is redrawing without the act; the detail dialog for it
        // closes because `selected` resolves to null, and this line is what
        // tells her why the thing she was editing has gone.
        setSelectedId(null);
        setFailure(
          t(
            'carePlan.conflictRemoved',
            'Somebody else removed this intervention. The board now shows the current plan.',
          ),
        );
        return;
      }
      setFailure(t('carePlan.commandFailed', 'The change was not saved. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const openAdd = (disciplineId: string, day: CarePlanDay) => {
    setAddAt({ disciplineId, day });
    setAddTitle('');
    setAddSchedule({ ...EMPTY_DRAFT, day });
    setAddAssignee('');
    setFailure(null);
  };

  const openDetail = (intervention: CarePlanItem) => {
    setSelectedId(intervention.id);
    setEditSchedule({
      day: intervention.day,
      time: intervention.time,
      duration_minutes: String(intervention.duration_minutes),
    });
    setFailure(null);
  };

  const performerName = (id: string) => performers.find((p) => p.id === id)?.name ?? '';

  const submitAdd = () => {
    if (!addAt) return;
    const duration = Number.parseInt(addSchedule.duration_minutes, 10);
    // The server refuses each of these by name. Checking here is not a second
    // validation: it is what keeps the nurse's typing in front of her instead
    // of sending a request whose refusal she would read as a save failure.
    if (!addTitle.trim() || !addSchedule.time || !Number.isFinite(duration) || duration <= 0) {
      setFailure(t('carePlan.addIncomplete', 'Give the intervention a title, a time and a length.'));
      return;
    }
    // The two assignee fields travel together or not at all: the category's
    // item schema requires each when the other is present, so an unassigned act
    // carries neither rather than two empty strings.
    const item: CarePlanItem = {
      id: randomUUID(),
      title: addTitle.trim(),
      discipline_id: addAt.disciplineId,
      day: addSchedule.day,
      time: addSchedule.time,
      duration_minutes: duration,
      ...(addAssignee
        ? { assignee_id: addAssignee, assignee_name: performerName(addAssignee) }
        : {}),
    };
    void runIntent({ verb: 'add-intervention', item }, () => setAddAt(null));
  };

  const submitRetime = () => {
    if (!selected) return;
    const duration = Number.parseInt(editSchedule.duration_minutes, 10);
    if (!editSchedule.time || !Number.isFinite(duration) || duration <= 0) {
      setFailure(t('carePlan.retimeIncomplete', 'Give the intervention a time and a length.'));
      return;
    }
    void runIntent(
      {
        verb: 'retime',
        itemId: selected.id,
        day: editSchedule.day,
        time: editSchedule.time,
        duration_minutes: duration,
      },
      () => setFailure(null),
    );
  };

  const submitAssign = (assigneeId: string) => {
    if (!selected) return;
    void runIntent(
      {
        verb: 'assign',
        itemId: selected.id,
        assignee_id: assigneeId,
        // Empty for both, or a real pair. A name with no id behind it is a
        // person nobody can look up, and the server refuses that pair.
        assignee_name: assigneeId ? performerName(assigneeId) : '',
      },
      () => setFailure(null),
    );
  };

  const submitRemove = () => {
    if (!selected) return;
    void runIntent({ verb: 'remove-intervention', itemId: selected.id }, () =>
      setSelectedId(null),
    );
  };

  const copyLink = () => {
    // The board's own URL. On the satellite that is
    // care-planner.<tenant>/stays/{id}; in the main app's frame it is
    // /home/a/care-planner/stays/{id}. Both are real deep links to this board,
    // which is why the address bar is the right source and no URL builder is.
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => setLinkCopied(true))
      // No false "Copied" when the clipboard is unavailable.
      .catch(() => undefined);
  };

  const cellInterventions = (disciplineId: string, day: CarePlanDay) =>
    items
      .filter((iv) => iv.discipline_id === disciplineId && iv.day === day)
      .sort(byTime);

  /**
   * What happened to this activity this week, or nothing at all.
   *
   * SCHEDULED DRAWS NOTHING, and that is the point. An activity still ahead of
   * the nurse is the normal case; badging it would put a mark on every cell of
   * a plan nobody has touched yet and leave her hunting for the ones that
   * matter. Only a state somebody's action put the activity in is shown.
   *
   * `missed` and `cancelled` are deliberately different words on different
   * tones: cancelled says it was called off beforehand, missed says it was
   * still expected and did not happen. Collapsing them would erase the
   * distinction the record exists to keep.
   */
  const ExecutionBadge = ({ status }: { status: OccurrenceStatus | undefined }) => {
    if (!status || status === 'scheduled') return null;
    const shown: Record<Exclude<OccurrenceStatus, 'scheduled'>, { label: string; tone: string }> = {
      in_progress: { label: t('carePlan.executionInProgress', 'In progress'), tone: 'warn' },
      completed: { label: t('carePlan.executionCompleted', 'Done'), tone: 'ok' },
      cancelled: { label: t('carePlan.executionCancelled', 'Cancelled'), tone: 'mute' },
      missed: { label: t('carePlan.executionMissed', 'Missed'), tone: 'bad' },
    };
    const { label, tone } = shown[status];
    return (
      <span
        className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
        style={{
          color: `var(--color-tone-${tone})`,
          backgroundColor: `var(--color-tone-${tone}-tint)`,
        }}
      >
        {label}
      </span>
    );
  };

  const interventionCard = (intervention: CarePlanItem) => (
    <button
      type="button"
      key={intervention.id}
      onClick={() => openDetail(intervention)}
      className="w-full space-y-1 rounded-[12px] border border-[var(--color-surface-border)] bg-[var(--color-neutral-50)] p-3 text-left transition active:scale-95 hover:bg-[var(--color-neutral-100)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]"
    >
      <div className="flex items-baseline gap-2 text-[12px] font-medium text-[var(--color-surface-mute)]">
        <span>{intervention.time}</span>
        <span>
          {t('carePlan.durationMinutes', '{{minutes}} min', {
            minutes: intervention.duration_minutes,
          })}
        </span>
        <ExecutionBadge status={execution.get(intervention.id)} />
      </div>
      <div className="text-[14px] font-semibold text-[var(--color-surface-ink)]">
        {intervention.title}
      </div>
      <div className="text-[12px] text-[var(--color-surface-mute)]">
        {intervention.assignee_name || t('carePlan.unassigned', 'Unassigned')}
      </div>
    </button>
  );

  const addButton = (disciplineId: string, day: CarePlanDay) =>
    isEditable && (
      <button
        type="button"
        onClick={() => openAdd(disciplineId, day)}
        aria-label={t('carePlan.addIntervention', 'Add intervention')}
        className="flex h-11 w-full items-center justify-center rounded-[12px] border-2 border-dashed border-[var(--color-surface-border)] text-[var(--color-surface-mute)] transition hover:border-[var(--color-primary-400)] hover:bg-[var(--color-primary-50)] hover:text-[var(--color-primary-600)]"
      >
        <Plus className="h-5 w-5" />
      </button>
    );

  const scheduleFields = (draft: ScheduleDraft, set: (next: ScheduleDraft) => void) => (
    <div className="grid grid-cols-3 gap-3">
      <label className="block text-[14px] font-medium text-[var(--color-surface-ink)]">
        {t('carePlan.fieldDay', 'Day')}
        <select
          value={draft.day}
          onChange={(e) => set({ ...draft, day: e.target.value as CarePlanDay })}
          className="mt-1.5 min-h-11 w-full rounded-[12px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-3 text-[14px] text-[var(--color-surface-ink)]"
        >
          {CARE_PLAN_DAYS.map((day) => (
            <option key={day} value={day}>
              {dayLabels[day]}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-[14px] font-medium text-[var(--color-surface-ink)]">
        {t('carePlan.fieldTime', 'Time')}
        <Input
          type="time"
          value={draft.time}
          onChange={(e) => set({ ...draft, time: e.target.value })}
          className="mt-1.5 min-h-11"
        />
      </label>
      <label className="block text-[14px] font-medium text-[var(--color-surface-ink)]">
        {t('carePlan.fieldDuration', 'Minutes')}
        <Input
          type="number"
          min={1}
          value={draft.duration_minutes}
          onChange={(e) => set({ ...draft, duration_minutes: e.target.value })}
          className="mt-1.5 min-h-11"
        />
      </label>
    </div>
  );

  const performerPicker = (value: string, onChange: (id: string) => void) => (
    <label className="block text-[14px] font-medium text-[var(--color-surface-ink)]">
      {t('carePlan.fieldAssignee', 'Performed by')}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 min-h-11 w-full rounded-[12px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-3 text-[14px] text-[var(--color-surface-ink)]"
      >
        <option value="">{t('carePlan.unassigned', 'Unassigned')}</option>
        {performers.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="h-full overflow-auto bg-[var(--color-surface-canvas,#ffffff)]">
      <div className="space-y-3 border-b border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            {/* Patient identity is the config-driven label of the real patient
                this stay belongs to. A patient has no name, so this is read-only
                and never an agent's free text. */}
            <h1
              className="text-[24px] font-bold tracking-[-0.01em] text-[var(--color-surface-ink)]"
              aria-label={t('carePlan.patientLabelAria', 'Patient')}
            >
              {patientLabel ?? t('carePlan.title', 'Care plan')}
            </h1>
            {plan && (
              <p className="mt-0.5 text-[14px] text-[var(--color-surface-mute)]">
                {t('carePlan.version', 'Version {{version}}', { version: plan.version })}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={copyLink}
            aria-label={t('carePlan.copyLink', 'Copy link')}
            className="min-h-11 gap-1.5 px-3 text-[13px]"
          >
            {linkCopied ? (
              <Check className="h-3.5 w-3.5 text-[var(--color-tone-ok)]" />
            ) : (
              <Link className="h-3.5 w-3.5" />
            )}
            <span className="hidden sm:inline">
              {linkCopied ? t('carePlan.linkCopied', 'Copied') : t('carePlan.copyLink', 'Copy link')}
            </span>
          </Button>
        </div>
        {failure && (
          <p role="alert" className="text-[14px] text-[var(--color-status-danger,#b3261e)]">
            {failure}
          </p>
        )}
      </div>

      {rows.length === 0 ? (
        // No roles and nothing planned. Not an error and not an empty grid with
        // no rows in it: the org has no clinical roles yet, which is a fact a
        // nurse can act on, and inventing a discipline to draw would be a
        // vocabulary nobody declared.
        <div className="flex flex-col items-center gap-2 p-10 text-center">
          <p className="text-[17px] text-[var(--color-surface-mute)]">
            {t('carePlan.noDisciplines', 'This organisation has no clinical roles yet.')}
          </p>
          <p className="max-w-sm text-[14px] text-[var(--color-surface-mute)]">
            {t(
              'carePlan.noDisciplinesHint',
              'A care plan is planned per discipline, and a discipline is a role of the organisation. Ask an administrator to create them.',
            )}
          </p>
        </div>
      ) : (
        <>
          {/* Phone (< md): one day at a time, top to bottom. A seven-column grid
              is illegible at 390px and a horizontal pan-around board was the
              original defect, so the phone never pans sideways. */}
          <div className="md:hidden">
            <div className="border-b border-[var(--color-surface-border)] bg-[var(--color-surface-bg)]">
              <div
                className="flex gap-1 overflow-x-auto p-2"
                role="tablist"
                aria-label={t('carePlan.daySelector', 'Day')}
              >
                {CARE_PLAN_DAYS.map((day) => (
                  <button
                    key={day}
                    type="button"
                    role="tab"
                    aria-selected={day === activeDay}
                    onClick={() => setActiveDay(day)}
                    className={cn(
                      'min-h-11 flex-shrink-0 rounded-full px-4 text-[14px] font-medium transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]',
                      day === activeDay
                        ? 'bg-[var(--color-primary-500)] text-[var(--color-neutral-0)]'
                        : 'bg-[var(--color-neutral-100)] text-[var(--color-surface-ink)]',
                    )}
                  >
                    {dayLabels[day]}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-3 p-4">
              {rows.map((row) => {
                const interventions = cellInterventions(row.id, activeDay);
                return (
                  <section
                    key={row.id}
                    className="rounded-[18px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] p-3"
                  >
                    <h2 className="mb-2 truncate text-[14px] font-semibold text-[var(--color-surface-ink)]">
                      {row.name}
                    </h2>
                    <div className="space-y-2">
                      {interventions.length === 0 && !isEditable && (
                        <p className="px-1 py-2 text-[12px] text-[var(--color-surface-mute)]">
                          {t('carePlan.nothingPlanned', 'Nothing planned')}
                        </p>
                      )}
                      {interventions.map(interventionCard)}
                      {addButton(row.id, activeDay)}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>

          {/* The whole week (md+). Columns flex; on a small tablet the board
              scrolls sideways as a deliberate secondary affordance. */}
          <div className="hidden overflow-x-auto p-4 md:block">
            <div className="min-w-[900px] lg:min-w-0">
              <div className="grid grid-cols-[160px_repeat(7,1fr)] gap-3 lg:grid-cols-[200px_repeat(7,1fr)]">
                <div />
                {CARE_PLAN_DAYS.map((day) => (
                  <div
                    key={day}
                    className="pb-2 text-center text-[14px] font-semibold text-[var(--color-surface-ink)]"
                  >
                    {dayLabels[day]}
                  </div>
                ))}
                {rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <div className="flex items-center py-3 text-[14px] font-medium text-[var(--color-surface-ink)]">
                      <span className="min-w-0 truncate">{row.name}</span>
                    </div>
                    {CARE_PLAN_DAYS.map((day) => (
                      <div
                        key={`${row.id}-${day}`}
                        className="min-h-[120px] space-y-2 rounded-[18px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] p-2"
                      >
                        {cellInterventions(row.id, day).map(interventionCard)}
                        {addButton(row.id, day)}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add an intervention. */}
      <Dialog open={addAt !== null} onOpenChange={(open) => !open && setAddAt(null)}>
        <DialogContent className="max-h-[85dvh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('carePlan.addIntervention', 'Add intervention')}</DialogTitle>
            <DialogDescription className="sr-only">
              {t('carePlan.addIntervention', 'Add intervention')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <label className="block text-[14px] font-medium text-[var(--color-surface-ink)]">
              {t('carePlan.fieldTitle', 'What is planned')}
              <Input
                value={addTitle}
                onChange={(e) => setAddTitle(e.target.value)}
                placeholder={t('carePlan.fieldTitlePlaceholder', 'Brief title…')}
                className="mt-1.5 min-h-11"
              />
            </label>
            {scheduleFields(addSchedule, setAddSchedule)}
            {performerPicker(addAssignee, setAddAssignee)}
            {failure && (
              <p role="alert" className="text-[14px] text-[var(--color-status-danger,#b3261e)]">
                {failure}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="pill" onClick={() => setAddAt(null)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button size="pill" onClick={submitAdd} disabled={busy}>
                {busy ? t('carePlan.saving', 'Saving…') : t('common.save', 'Save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* One intervention: retime it, give it a performer, or take it off the plan. */}
      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelectedId(null)}>
        <DialogContent className="max-h-[85dvh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.title}</DialogTitle>
                <DialogDescription className="sr-only">
                  {t('carePlan.interventionDetails', 'Intervention details')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {scheduleFields(editSchedule, setEditSchedule)}
                <div className="flex justify-end">
                  <Button size="pill" onClick={submitRetime} disabled={!isEditable || busy}>
                    {t('carePlan.retime', 'Move')}
                  </Button>
                </div>
                <div className="border-t border-[var(--color-surface-border)] pt-4">
                  {/* An unassigned item carries no assignee_id at all, which the picker reads as nobody chosen. */}
                  {performerPicker(selected.assignee_id ?? '', (id) => submitAssign(id))}
                </div>
                {failure && (
                  <p role="alert" className="text-[14px] text-[var(--color-status-danger,#b3261e)]">
                    {failure}
                  </p>
                )}
                <div className="flex justify-between border-t border-[var(--color-surface-border)] pt-4">
                  <Button
                    variant="outline"
                    size="pill"
                    onClick={submitRemove}
                    disabled={!isEditable || busy}
                    className="gap-1.5 text-[var(--color-status-danger,#b3261e)]"
                  >
                    <Trash2 className="h-4 w-4" />
                    {t('carePlan.remove', 'Remove')}
                  </Button>
                  <Button variant="outline" size="pill" onClick={() => setSelectedId(null)}>
                    {t('actions.close', 'Close')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
